<template>
    <HashScrollComponent>
        <div
            id="app"
            class="App">
            <template v-if="isUserLogged">
                <ErrorLayout v-if="isGlobalError" />
                <DefaultLayout v-else />
            </template>
        </div>
    </HashScrollComponent>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import _throttle from 'lodash/throttle.js';
import { redirectToAuth } from '@/utils/utils.js';
import { userActionTypes, userGetterTypes } from '@/store/modules/user.js';
import YandexMetrika from '@/mixins/YandexMetrika.js';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import ErrorLayout from '@/layouts/ErrorLayout.vue';
import HashScrollComponent from './components/HashScrollComponent.vue';

const {
    mapGetters: mapUserGetters,
    mapActions: mapUserActions,
} = createNamespacedHelpers('user');
const RESIZE_THROTTLE_MS = 150;
const SCROLL_THROTTLE_MS = 300;
const BASE_SCROLLBAR_WIDTH = 5;
let lastScrollTop = 0;

export default {
    name: 'App',
    components: {
        DefaultLayout,
        ErrorLayout,
        HashScrollComponent,
    },
    mixins: [YandexMetrika],
    computed: {
        ...mapGetters({
            isGlobalError: 'globals/isGlobalError',
            scrollDirection: 'globals/scrollDirection',
            scrollDirections: 'globals/scrollDirections',
            scrollPosition: 'globals/scrollPosition',
            scrollPositions: 'globals/scrollPositions',
        }),
        ...mapUserGetters({
            isUserLogged: userGetterTypes.GET_LOGGED,
            user: userGetterTypes.GET_USER,
        }),
    },
    methods: {
        ...mapActions({
            removeGlobalError: 'globals/removeGlobalError',
            setWinWidth: 'globals/setWinWidth',
            setScrollbarWidth: 'globals/setScrollbarWidth',
            setScrollDirection: 'globals/setScrollDirection',
            setScrollPosition: 'globals/setScrollPosition',
        }),
        ...mapUserActions({
            getUser: userActionTypes.GET_USER,
        }),
        async checkAuth() {
            const isUserExists = Object.values(this.user).length;

            if (!isUserExists) await this.getUser();

            if (this.isUserLogged) return;

            console.error('[user] user is not logged in');
            redirectToAuth();
        },
        setGlobals() {
            this.setWinWidth(window.innerWidth);

            const scrollbarWidth = this.$utils.getScrollbarWidth();

            this.setScrollbarWidth(scrollbarWidth);
            document.documentElement.style.setProperty(
                '--scrollbar-width',
                `${scrollbarWidth || BASE_SCROLLBAR_WIDTH}px`,
            );
        },
        calcScrollDirection() {
            const scrollDirection = window.pageYOffset > lastScrollTop
                ? this.scrollDirections.DOWN
                : this.scrollDirections.UP;

            if (scrollDirection !== this.scrollDirection) {
                this.setScrollDirection(scrollDirection);
            }

            lastScrollTop = window.pageYOffset;
        },
        calcScrollPosition() {
            const {
                scrollHeight,
                clientHeight,
            } = document.documentElement;

            let scrollPosition = this.scrollPositions.MIDDLE;

            if (window.pageYOffset <= 0) {
                scrollPosition = this.scrollPositions.TOP;
            } else if (window.pageYOffset + clientHeight >= scrollHeight) {
                scrollPosition = this.scrollPositions.BOTTOM;
            }

            if (scrollPosition !== this.scrollPosition) {
                this.setScrollPosition(scrollPosition);
            }
        },
        windowResizeHandler() {
            this.setGlobals();
        },
        windowScrollHandler() {
            this.calcScrollDirection();
            this.calcScrollPosition();
        },
    },
    async created() {
        this.windowResizeHandler = _throttle(
            this.windowResizeHandler,
            RESIZE_THROTTLE_MS,
        );
        this.windowScrollHandler = _throttle(
            this.windowScrollHandler,
            SCROLL_THROTTLE_MS,
        );

        try {
            await this.checkAuth();
            this.removeGlobalError();
        } catch (error) {
            console.error(error);
        }
    },
    mounted() {
        this.setGlobals();
        this.calcScrollPosition();

        window.addEventListener('resize', this.windowResizeHandler);
        window.addEventListener('orientationchange', this.windowResizeHandler);
        window.addEventListener('scroll', this.windowScrollHandler);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.windowResizeHandler);
        window.removeEventListener('orientationchange', this.windowResizeHandler);
        window.removeEventListener('scroll', this.windowScrollHandler);
    },
};
</script>

<style lang="scss">
.App {
    display: flex;
    flex-direction: column;
    scroll-behavior: smooth;
    min-height: 100vh;
}

.App__Content {
    max-height: 100vh;
}
</style>
